import { AppContext } from '@prisjakt/ui-app-context';
import React, { useState, useCallback, useContext } from 'react';
import { HorizontalScroll } from 'src/components/horizontal-scroll';
import { TitleSmallText } from '@prisjakt/ui/text/variants';
import Viewport from '../../../../components/viewport';
import NavigationLink from './link';
import { MediaButton } from 'src/components/media-button';
import * as S from './styles';
import { FacetLink } from './types';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

const DEFAULT_ITEM_LIMIT = 7;

type NavigationLinksProps = {
  links: FacetLink[];
  onClick?: (link: FacetLink, index: number) => void;
  onClickPreventDefault: boolean;
  _showAll?: boolean;
  asListItems?: boolean;
  itemLimit?: number;
};

const renderNavigationLinks = ({
  links,
  onClick,
  onClickPreventDefault,
  _showAll = true,
  asListItems = false,
  itemLimit = DEFAULT_ITEM_LIMIT,
}: NavigationLinksProps) =>
  links && links.length
    ? links.map((link, index) => {
        const selected = link.selected;
        const Component: React.ComponentType<React.PropsWithChildren<
          React.PropsWithChildren<S.ListItemProps>
        >> = asListItems ? S.ListItem : S.ListItemDiv;

        return link.path ? (
          <NavigationLink
            key={link.value}
            link={link}
            selected={selected}
            onClick={facetLink => onClick?.(facetLink, index)}
            onClickPreventDefault={onClickPreventDefault}
            // Lazy loading is disabled for all initially visible images to enable them
            // to load before React is loaded. The goal of this strategy is to improve
            // our Lighthouse performance score, and specifically the "Largest Contentful
            // Paint" metric.
            lazyLoadImage={index >= itemLimit}
            Component={Component}
          />
        ) : null;
      })
    : null;

type Props = {
  onClick?: (link: FacetLink) => void;
  onClickPreventDefault: boolean;
  links: FacetLink[];
  itemLimit?: number;
  showAll?: boolean;
};
export const NavigationSliceView = ({
  links,
  onClick,
  onClickPreventDefault = false,
  itemLimit = DEFAULT_ITEM_LIMIT,
  showAll = false,
}: Props) => {
  const { t } = useContext(AppContext);
  const [_showAll, _setShowAll] = useState(false);
  const onShowAll = useCallback(() => _setShowAll(!_showAll), [
    _showAll,
    _setShowAll,
  ]);

  const trackOnClick = (link: FacetLink, index: number) => {
    if (link.path)
      trackInternalNavigationClick({
        scope: InternalNavigationScope.NAVIGATION_CAROUSEL,
        nextPage: link.path,
        index,
      });
    onClick?.(link);
  };

  const mobileLinks = renderNavigationLinks({
    links,
    onClick: trackOnClick,
    onClickPreventDefault,
    itemLimit,
  });
  const desktopLinks = renderNavigationLinks({
    links,
    onClick: trackOnClick,
    onClickPreventDefault,
    _showAll,
    asListItems: true,
    itemLimit,
  });

  return mobileLinks || desktopLinks ? (
    <S.Wrapper>
      {mobileLinks && (
        <Viewport mobile tablet>
          <HorizontalScroll
            itemWidth={110}
            loadingHeight={140}
            showArrows={false}
            itemHint
          >
            {mobileLinks}
          </HorizontalScroll>
        </Viewport>
      )}
      {desktopLinks && (
        <Viewport desktop>
          <S.ListWrapper>
            <S.List $showAll={showAll || _showAll} $itemLimit={itemLimit}>
              {desktopLinks}
            </S.List>
            {links && links.length > itemLimit && !showAll && (
              <S.ListAction>
                <MediaButton
                  onClick={onShowAll}
                  media={
                    <S.ListActionButton>
                      <TitleSmallText>
                        {t(
                          'common',
                          _showAll ? 'button.show-less' : 'button.show-more'
                        )}
                      </TitleSmallText>
                    </S.ListActionButton>
                  }
                />
              </S.ListAction>
            )}
          </S.ListWrapper>
        </Viewport>
      )}
    </S.Wrapper>
  ) : null;
};
